import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import {
  Container,
  Card,
  Button,
  Spinner,
  Nav,
  Row,
  Col,
  Form,
  Alert,
} from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";

import Header_Manager from "../../Components/Header_Manager";
import AssignPackageToCompanyModal from "../../Components/ManagerDetails/AssignPackageToCompanyModal";
import AssignPackageModal from "../../Components/CompanyDetails/AssignPackageModal";
import { BASE_URL } from "../../Config";
import "./Manager.css";

const TABS = {
  MY_PACKAGES: "myPackages",
  COMPANY_PACKAGES: "companyPackages",
  AGENT_PACKAGES: "agentPackages",
};

const normalizePackages = (payload) => {
  if (Array.isArray(payload?.packages)) return payload.packages;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getPackageDisplayName = (pkg, fallback = "-") => {
  const packageName = pkg.packageName ?? pkg.name ?? fallback;
  return `${packageName}${pkg.companyProduct ? ` (${pkg.companyProduct})` : ""}`;
};

const Manager_Packages = () => {
  const [activeTab, setActiveTab] = useState(TABS.MY_PACKAGES);
  const [managerId, setManagerId] = useState("");
  const [managerPackages, setManagerPackages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [companyPackages, setCompanyPackages] = useState([]);
  const [agentPackages, setAgentPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingCompanyPkgs, setLoadingCompanyPkgs] = useState(false);
  const [loadingAgentPkgs, setLoadingAgentPkgs] = useState(false);
  const [showAssignCompanyModal, setShowAssignCompanyModal] = useState(false);
  const [showAssignAgentModal, setShowAssignAgentModal] = useState(false);

  const selectedCompanyName =
    companies.find((c) => c.companyProfileId === selectedCompanyId)?.name || "";

  const loadManagerPackages = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}ManagerPackages?id=${id}`, {
        headers: { accept: "*/*" },
      });

      if (response.data.responseCode === "0000") {
        setManagerPackages(normalizePackages(response.data));
      } else {
        setManagerPackages([]);
        Swal.fire(
          "Info",
          response.data.message || "No packages are assigned to this manager.",
          "info"
        );
      }
    } catch (error) {
      console.error(error);
      setManagerPackages([]);
      Swal.fire("Error", "Failed to load manager packages.", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadAssignedCompanies = async (managerUserId) => {
    if (!managerUserId) return;
    setLoadingCompanies(true);
    try {
      const response = await axios.get(
        `${BASE_URL}AssignedCompany?ManagerId=${managerUserId}`
      );
      if (
        response.data.responseCode === "0000" &&
        Array.isArray(response.data.companyDetails)
      ) {
        const list = response.data.companyDetails;
        setCompanies(list);
        const user = JSON.parse(sessionStorage.getItem("user") || "{}");
        const preferred =
          user.selectedCompanyId || user.companyId || list[0]?.companyProfileId || "";
        const exists = list.some((c) => c.companyProfileId === preferred);
        setSelectedCompanyId(exists ? preferred : list[0]?.companyProfileId || "");
      } else {
        setCompanies([]);
        setSelectedCompanyId("");
      }
    } catch (error) {
      console.error(error);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadCompanyPackages = async (companyId) => {
    if (!companyId) {
      setCompanyPackages([]);
      return;
    }
    setLoadingCompanyPkgs(true);
    try {
      const response = await axios.get(
        `${BASE_URL}RetailCompanyPackage?id=${companyId}`,
        { headers: { accept: "*/*" } }
      );
      if (response.data.responseCode === "0000") {
        setCompanyPackages(normalizePackages(response.data));
      } else {
        setCompanyPackages([]);
      }
    } catch (error) {
      console.error(error);
      setCompanyPackages([]);
      Swal.fire("Error", "Failed to load company packages.", "error");
    } finally {
      setLoadingCompanyPkgs(false);
    }
  };

  const loadAgentPackages = async (companyId) => {
    if (!companyId) {
      setAgentPackages([]);
      return;
    }
    setLoadingAgentPkgs(true);
    try {
      const response = await axios.get(`${BASE_URL}AgentPackages?id=${companyId}`, {
        headers: { accept: "*/*" },
      });
      if (response.data.responseCode === "0000") {
        setAgentPackages(response.data.data || []);
      } else {
        setAgentPackages([]);
      }
    } catch (error) {
      console.error(error);
      setAgentPackages([]);
      Swal.fire("Error", "Failed to load agent packages.", "error");
    } finally {
      setLoadingAgentPkgs(false);
    }
  };

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    if (!user.userId) {
      Swal.fire("Error", "Your session has expired. Please sign in again.", "error");
      return;
    }
    setManagerId(user.userId);
    loadManagerPackages(user.userId);
    loadAssignedCompanies(user.userId);
  }, []);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const company = companies.find((c) => c.companyProfileId === selectedCompanyId);
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    sessionStorage.setItem(
      "user",
      JSON.stringify({
        ...user,
        companyId: selectedCompanyId,
        selectedCompanyId,
        selectedCompanyName: company?.name || "",
        companyName: company?.name || "",
      })
    );

    if (activeTab === TABS.COMPANY_PACKAGES) {
      loadCompanyPackages(selectedCompanyId);
    }
    if (activeTab === TABS.AGENT_PACKAGES) {
      loadAgentPackages(selectedCompanyId);
    }
  }, [selectedCompanyId, activeTab, companies]);

  const handleEditAgentPackage = async (pkgId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}EditAgentPackages?pkgId=${pkgId}`,
        {},
        { headers: { accept: "*/*" } }
      );
      if (response.data.responseCode === "0000") {
        Swal.fire("Success", response.data.message || "Package updated.", "success");
        loadAgentPackages(selectedCompanyId);
      } else {
        Swal.fire("Error", response.data.message || "Unable to update package.", "error");
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Unable to update package.",
        "error"
      );
    }
  };

  const managerColumns = [
    {
      name: "Package Name",
      selector: (row) => getPackageDisplayName(row),
      sortable: true,
      wrap: true,
    },
    {
      name: "Duration",
      selector: (row) => row.packageDuration ?? row.duration ?? "-",
      sortable: true,
    },
    {
      name: "Price",
      selector: (row) =>
        row.packagePrice != null ? `Rs. ${row.packagePrice}` : row.price ?? "-",
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.packageStatus ?? row.status ?? "-",
      sortable: true,
      cell: (row) => {
        const status = row.packageStatus ?? row.status ?? "-";
        const active = String(status).toLowerCase() === "active";
        return (
          <span className={`badge ${active ? "text-bg-success" : "text-bg-secondary"}`}>
            {status}
          </span>
        );
      },
    },
  ];

  const companyColumns = [
    {
      name: "Package Name",
      selector: (row) => getPackageDisplayName(row),
      sortable: true,
      wrap: true,
    },
    {
      name: "Duration",
      selector: (row) => row.packageDuration ?? "-",
      sortable: true,
    },
    {
      name: "Price",
      selector: (row) =>
        row.packagePrice != null ? `Rs. ${row.packagePrice}` : "-",
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.packageStatus ?? "-",
      cell: (row) => (
        <span
          className={`badge ${
            row.packageStatus === "Active" ? "text-bg-success" : "text-bg-danger"
          }`}
        >
          {row.packageStatus}
        </span>
      ),
    },
  ];

  const agentColumns = [
    {
      name: "Agent Name",
      selector: (row) => row.agentName ?? "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Package Name",
      selector: (row) => getPackageDisplayName(row),
      sortable: true,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => row.status ?? "-",
      cell: (row) => (
        <span
          className={`badge ${row.status === "Active" ? "text-bg-success" : "text-bg-danger"}`}
        >
          {row.status}
        </span>
      ),
    },
    {
      name: "Price",
      selector: (row) => row.packagePrice ?? "-",
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Button
          size="sm"
          variant={row.status === "Active" ? "outline-danger" : "outline-success"}
          onClick={() => handleEditAgentPackage(row.id)}
        >
          {row.status === "Active" ? "Deactivate" : "Activate"}
        </Button>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  const CompanySelectBar = () => (
    <Card className="mgr-card mb-3">
      <Card.Body className="py-3">
        <Row className="g-2 align-items-end">
          <Col xs={12} md={8}>
            <Form.Label className="fw-semibold mb-1">Select Company</Form.Label>
            <Form.Select
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              disabled={loadingCompanies}
              size="lg"
            >
              <option value="">Select Company</option>
              {companies.map((c) => (
                <option key={c.companyProfileId} value={c.companyProfileId}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={4}>
            <Button
              variant="outline-secondary"
              className="w-100"
              onClick={() => loadAssignedCompanies(managerId)}
              disabled={loadingCompanies}
            >
              {loadingCompanies ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" /> Loading
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-1" /> Refresh
                </>
              )}
            </Button>
          </Col>
        </Row>
        {selectedCompanyId && selectedCompanyName && (
          <Alert variant="primary" className="mt-3 mb-0 py-2">
            Selected: <strong>{selectedCompanyName}</strong>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );

  return (
    <div className="mgr-page">
      <Header_Manager />

      <Container fluid="xl" className="px-3 px-md-4">
        <div className="mgr-hero d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div>
            <h4 className="fw-semibold mb-1">
              <i className="bi bi-box-seam me-2" />
              Package Management
            </h4>
            <p className="mb-0">
              View your packages, assign them to companies, then assign to agents.
            </p>
          </div>
          <Button variant="light" onClick={() => setShowAssignCompanyModal(true)}>
            <i className="bi bi-building-add me-1" />
            Assign to Company
          </Button>
        </div>

        <Nav variant="pills" className="mgr-step-nav gap-2 mb-3 flex-nowrap">
          <Nav.Item>
            <Nav.Link
              active={activeTab === TABS.MY_PACKAGES}
              onClick={() => setActiveTab(TABS.MY_PACKAGES)}
            >
              <i className="bi bi-collection me-1" />
              My Packages
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeTab === TABS.COMPANY_PACKAGES}
              onClick={() => setActiveTab(TABS.COMPANY_PACKAGES)}
            >
              <i className="bi bi-building me-1" />
              Company Packages
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeTab === TABS.AGENT_PACKAGES}
              onClick={() => setActiveTab(TABS.AGENT_PACKAGES)}
            >
              <i className="bi bi-person-badge me-1" />
              Agent Packages
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {activeTab === TABS.MY_PACKAGES && (
          <Card className="mgr-card">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">
                <i className="bi bi-list-ul me-2 text-primary" />
                Assigned to Manager
              </h5>
              <div className="d-flex gap-2">
                <span className="badge text-bg-light border align-self-center">
                  {managerPackages.length} total
                </span>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => loadManagerPackages(managerId)}
                >
                  <i className="bi bi-arrow-clockwise me-1" />
                  Refresh
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="text-muted mt-2 mb-0">Loading packages...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <DataTable
                    columns={managerColumns}
                    data={managerPackages}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    noDataComponent={
                      <div className="py-4 text-muted">
                        <i className="bi bi-inbox fs-3 d-block mb-2" />
                        No packages assigned to this manager
                      </div>
                    }
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {activeTab === TABS.COMPANY_PACKAGES && (
          <>
            <CompanySelectBar />
            {!selectedCompanyId ? (
              <Alert variant="warning">
                Please select a company to view its packages.
              </Alert>
            ) : (
              <Card className="mgr-card">
                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0">Packages for {selectedCompanyName}</h5>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => setShowAssignCompanyModal(true)}
                  >
                    <i className="bi bi-plus-lg me-1" />
                    Assign Package
                  </Button>
                </Card.Header>
                <Card.Body>
                  {loadingCompanyPkgs ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <DataTable
                      columns={companyColumns}
                      data={companyPackages}
                      pagination
                      highlightOnHover
                      responsive
                      striped
                      noDataComponent={
                        <div className="py-4 text-muted">
                          No packages assigned to this company yet
                        </div>
                      }
                    />
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )}

        {activeTab === TABS.AGENT_PACKAGES && (
          <>
            <CompanySelectBar />
            {!selectedCompanyId ? (
              <Alert variant="warning">
                Please select a company to manage agent packages.
              </Alert>
            ) : (
              <Card className="mgr-card">
                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0">Agent Packages — {selectedCompanyName}</h5>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => setShowAssignAgentModal(true)}
                  >
                    <i className="bi bi-plus-lg me-1" />
                    Assign to Agent
                  </Button>
                </Card.Header>
                <Card.Body>
                  {loadingAgentPkgs ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <DataTable
                      columns={agentColumns}
                      data={agentPackages}
                      pagination
                      highlightOnHover
                      responsive
                      striped
                      noDataComponent={
                        <div className="py-4 text-muted">
                          No agent packages found for this company
                        </div>
                      }
                    />
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Container>

      <AssignPackageToCompanyModal
        show={showAssignCompanyModal}
        onHide={() => setShowAssignCompanyModal(false)}
        packages={managerPackages}
        companies={companies}
        onAssigned={() => {
          if (selectedCompanyId) loadCompanyPackages(selectedCompanyId);
        }}
      />

      {showAssignAgentModal && (
        <AssignPackageModal
          isOpen={showAssignAgentModal}
          onRequestClose={() => {
            setShowAssignAgentModal(false);
            loadAgentPackages(selectedCompanyId);
          }}
          userId={selectedCompanyId}
        />
      )}
    </div>
  );
};

export default Manager_Packages;
